import '@/global.css'
import { Input } from "@/components/ui/input"
import { Link } from "react-router";
import { ROUTE } from '@/routes/constants'
import { LoadingOverlay } from '@/components/ui/loading-overlay'
import { Theme, ThemeColors } from './login-theme'
import { EmailIcon, LockIcon, EyeIcon, LoginArrowIcon } from './login-icons'
import { useLogin } from './use-login'

interface LoginFormProps {
  theme?: Theme;
  colors: ThemeColors;
}

// 右侧表单列：按设计稿 1:1 绝对定位（外层 <form> 即定位容器，左边缘对齐设计稿 x=1272，宽 421）
// 表单逻辑（接口调用 / token 校验 / 校验状态）统一抽到 useLogin，移动端复用同一套逻辑。
function LoginForm({ colors }: LoginFormProps) {
  const {
    t, loginMessage, isLoading, isEmailInvalid, isPswInvalid,
    showPassword, setShowPassword, setEmailInvalid, setPswInvalid, onLogin,
  } = useLogin();

  const labelStyle: React.CSSProperties = { fontSize: 16, fontWeight: 500, color: '#575C60', lineHeight: '20px' };
  const inputStyle: React.CSSProperties = {
    width: '100%', height: 59, borderRadius: 28.5, background: colors.inputBg, border: 0,
    fontSize: 16, color: '#1F1F1F',
  };
  // 右侧表单：在设计稿位置（left:1272, 宽421）用 flex 纵向流式排版，
  // 起点锚定在设计稿 top:224；各组之间用 marginTop 复刻设计稿间距。
  // 改为流式后，英文等更长文案会自然向下顶开，不再与下方元素重叠（中文仍 1:1 还原设计稿）。
  return (
    <form
      action={onLogin}
      style={{
        position: 'absolute', left: 1272, top: 0, width: 421, height: 1080,
        display: 'flex', flexDirection: 'column', paddingTop: 224,
        boxSizing: 'border-box', pointerEvents: 'none',
      }}
    >
      <LoadingOverlay isLoading={isLoading} />

      {/* 标题 + 副标题 */}
      <h2 style={{ margin: 0, textAlign: 'center', fontSize: 60, fontWeight: 500, lineHeight: '64px', color: '#2A2F32', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
        {t("user.welcome-back")}
      </h2>
      <p style={{ marginTop: 12, marginBottom: 0, textAlign: 'center', fontSize: 20, fontWeight: 400, color: '#575C60', lineHeight: '24px' }}>
        {t("user.welcome-sub")}
      </p>
      {loginMessage && (
        <p style={{ marginTop: 8, marginBottom: 0, textAlign: 'center', fontSize: 14, color: '#EF4444', lineHeight: '18px' }}>{loginMessage}</p>
      )}

      {/* 電子郵箱 */}
      <label htmlFor="email" style={{ ...labelStyle, marginTop: 50 }}>{t("user.email")}</label>
      <div style={{ position: 'relative', marginTop: 10, height: 59, pointerEvents: 'auto' }}>
        <EmailIcon aria-hidden="true" style={{ position: 'absolute', left: 24, top: 18, width: 22, height: 22, zIndex: 1, color: '#575C60' }} />
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="scholar@atelier.edu.hk"
          required
          onInvalid={(e: any) => { setEmailInvalid(true); e.target.setCustomValidity(t("user.email-error")); }}
          onInput={(e: any) => { e.target.setCustomValidity(""); }}
          className="pl-14 pr-5 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
          style={{ ...inputStyle, outline: isEmailInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
        />
      </div>

      {/* 密碼 + 忘記密碼（同一行：标签居左、忘記密碼居右） */}
      <div style={{ marginTop: 19, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <label htmlFor="password" style={labelStyle}>{t("user.password")}</label>
        <Link
          to={ROUTE.REQUEST_RESET_PASSWORD}
          style={{ fontSize: 16, fontWeight: 400, color: colors.link, lineHeight: '16px', pointerEvents: 'auto' }}
          className="hover:underline"
        >
          {t("user.forgot-password")}?
        </Link>
      </div>
      <div style={{ position: 'relative', marginTop: 9, height: 59, pointerEvents: 'auto' }}>
        <LockIcon aria-hidden="true" style={{ position: 'absolute', left: 27, top: 19, width: 16, height: 21, zIndex: 1, color: '#575C60' }} />
        <Input
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          required
          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$"
          onInvalid={(e: any) => { setPswInvalid(true); e.target.setCustomValidity(t("user.password-error")); }}
          onInput={(e: any) => { e.target.setCustomValidity(""); }}
          className="pl-14 pr-14 placeholder:text-[rgba(115,119,123,0.5)] focus-visible:ring-2"
          style={{ ...inputStyle, outline: isPswInvalid ? '2px solid #F87171' : undefined, ['--tw-ring-color' as any]: colors.link + '66' }}
        />
        <button
          type="button"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? "Hide password" : "Show password"}
          style={{ position: 'absolute', right: 22, top: 22, width: 22, height: 15, pointerEvents: 'auto' }}
        >
          <EyeIcon aria-hidden="true" style={{ width: 22, height: 15, color: '#73777B' }} />
        </button>
      </div>

      {/* 登录按钮 */}
      <button
        type="submit"
        style={{
          marginTop: 23, width: '100%', height: 73, borderRadius: 36.5,
          background: colors.buttonBg, color: colors.buttonText,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontSize: 24, fontWeight: 500, border: 0, cursor: 'pointer',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)', pointerEvents: 'auto',
        }}
        className="transition-[filter] hover:brightness-[0.97]"
      >
        {t("user.sign-in-action")}
        <LoginArrowIcon aria-hidden="true" style={{ width: 20, height: 20, color: colors.buttonText }} />
      </button>

      {/* 分隔线 */}
      <div style={{ marginTop: 59, width: '100%', height: 1, background: '#E5E7EB' }} />

      {/* 注册引导 */}
      <div style={{ marginTop: 62, width: '100%', textAlign: 'center', fontSize: 16, color: '#575C60', lineHeight: '24px' }}>
        {t("user.login-comment")}
        <Link to={ROUTE.SIGNUP} style={{ marginLeft: 6, fontWeight: 500, color: colors.link, pointerEvents: 'auto' }} className="underline underline-offset-4">
          {t("user.sign-up")}
        </Link>
      </div>
    </form>
  )
}

export default LoginForm;
